import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthProvider';
import { BrandSelector } from '../dashboard/BrandSelector';
import type { BrandListItem } from '../dashboard/api';
import { AppSidebar } from './AppSidebar';
import { TopbarUser } from './UserMenu';

/**
 * Guscio applicativo ricalcato sull'Allegato A: sidebar navy, top bar con
 * selettore brand e tab dei livelli, area contenuto.
 *
 * `welcome-mode` è lo stato che il riferimento usa quando nessun brand è
 * selezionato: sidebar e top bar diventano scure e la navigazione si attenua.
 */
export type Level = 1 | 2 | 3;

interface AppShellProps {
  brands: BrandListItem[];
  brandsLoading: boolean;
  currentSlug: string | null;
  onSelectBrand: (slug: string) => void;
  /** Verticale del brand corrente, mostrato accanto al selettore. */
  sectorLabel?: string | null;
  level: Level;
  onLevelChange: (level: Level) => void;
  children: ReactNode;
}

const LEVELS: { level: Level; key: 'insight' | 'decision' | 'intelligence' }[] = [
  { level: 1, key: 'insight' },
  { level: 2, key: 'decision' },
  { level: 3, key: 'intelligence' },
];

export function AppShell({
  brands,
  brandsLoading,
  currentSlug,
  onSelectBrand,
  sectorLabel,
  level,
  onLevelChange,
  children,
}: AppShellProps): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();

  if (!user) return null;

  const welcomeMode = currentSlug === null;
  const enabledLevels = LEVELS.filter((l) => user.features[l.key]);

  return (
    <div className={welcomeMode ? 'app welcome-mode' : 'app'}>
      <AppSidebar user={user} active="dashboard" />

      <div className="main">
        <header className="topbar">
          <BrandSelector
            brands={brands}
            currentSlug={currentSlug}
            onSelect={onSelectBrand}
            loading={brandsLoading}
          />

          {sectorLabel ? <span className="sector-badge">{sectorLabel}</span> : null}

          {enabledLevels.length > 0 ? (
            <div className="level-tabs">
              {enabledLevels.map((l) => (
                <button
                  key={l.key}
                  type="button"
                  className={l.level === level ? 'level-tab active' : 'level-tab'}
                  // Senza un brand selezionato non c'è nulla da mostrare nei livelli.
                  disabled={welcomeMode}
                  onClick={() => onLevelChange(l.level)}
                >
                  {t(`levels.${l.key}`)}
                </button>
              ))}
            </div>
          ) : null}

          <TopbarUser user={user} />
        </header>

        <div className="content">{children}</div>
      </div>
    </div>
  );
}
